import * as React from 'react';
import { State } from '../State';
import { connect } from 'react-redux';
import { resolveDataSync, resolveData, onDataUpdate } from '../DynamicData';
import { allComponentsAreResolved, resolveComponentAndChildComponents } from '../resolve-component';

type PropType<T> = {
  data: Scope.DynamicData<T>;
  children: (data: T) => JSX.Element | JSX.Element[];
  /**
   * This lets you render something like a loading spinner during the time we resolve
   * the data. If you can you should render placeholders instead of spinners so
   * the experience is that the real data fades in once it's available.
   */
  fallback?: JSX.Element | JSX.Element[];
  /**
   * If you don't want to specify a different rendering as a fallback when the data is not
   * yet resolved you can pass this to render. If you specify both this and the `fallback`
   * prop this gets precedence.
   */
  fallbackData?: T;
  /**
   * This lets you defer resolving if you have a component that is rendered but visually hidden.
   * If you always pass `false` here the data will never be resolved. If you initially pass `false`
   * and then pass `true` once it's visible we'll resolve the data when it becomes visible.
   */
  shouldResolve?: boolean;
  onError?: (retry: () => Promise<void>) => JSX.Element | JSX.Element[];
  currentTheme?: string;
};

type StateType = {
  resolveFailed: boolean;
  componentsResolved: boolean;
};

class DynamicData<T> extends React.Component<PropType<T>, StateType> {
  unbindDataWacher: Function | null = null;
  constructor(props: PropType<T>) {
    super(props);

    const data = resolveDataSync(props.data);
    let componentsResolved = false;
    if (data !== null) {
      componentsResolved = allComponentsAreResolved(data, props.currentTheme);
    }

    this.state = {
      resolveFailed: false,
      componentsResolved,
    };
  }
  resolveComponents = (data: any, currentTheme: string | undefined) => {
    if (!allComponentsAreResolved(data)) {
      if (this.state.componentsResolved !== false) {
        this.setState({ componentsResolved: false });
      }
      return resolveComponentAndChildComponents(data, currentTheme).then(
        () => {
          this.setState({ componentsResolved: true });
        },
        () => {
          this.setState({ resolveFailed: true, componentsResolved: false });
        },
      );
    } else if (this.state.componentsResolved !== true) {
      this.setState({ componentsResolved: true });
    }
  };
  resolveData = (data: Scope.DynamicData<T>) => {
    return resolveData(data).then(
      d => {
        if (this.state.resolveFailed) {
          this.setState({ resolveFailed: false });
        }
        return this.resolveComponents(d, this.props.currentTheme);
      },
      e => {
        this.setState({ resolveFailed: true });
      },
    );
  };
  componentDidMount() {
    if (this.props.shouldResolve !== false) {
      const syncData = resolveDataSync(this.props.data);
      if (syncData === null) {
        this.resolveData(this.props.data);
      } else {
        this.resolveComponents(syncData, this.props.currentTheme);
      }
    }
    this.unbindDataWacher = onDataUpdate(this.props.data, () => this.forceUpdate());
  }
  UNSAFE_componentWillReceiveProps(nextProps: PropType<T>) {
    if (this.props.data !== nextProps.data) {
      if (this.unbindDataWacher) {
        this.unbindDataWacher();
      }
      this.unbindDataWacher = onDataUpdate(nextProps.data, () => this.forceUpdate());
    }

    if (nextProps.shouldResolve !== false) {
      const nextData = resolveDataSync(nextProps.data);
      if (nextData === null) {
        this.resolveData(nextProps.data);
      } else {
        this.resolveComponents(nextData, nextProps.currentTheme);
      }
    }
  }
  componentWillUnmount() {
    if (this.unbindDataWacher) {
      this.unbindDataWacher();
    }
  }
  render() {
    const loadedData = resolveDataSync(this.props.data);
    if (loadedData && this.state.componentsResolved) {
      return this.props.children(loadedData);
    }
    if (this.state.resolveFailed) {
      if (this.props.onError) {
        return this.props.onError(() => this.resolveData(this.props.data));
      } else {
        return null;
      }
    }
    if (this.props.fallbackData) {
      return this.props.children(this.props.fallbackData);
    }
    return this.props.fallback || null;
  }
}

export default (connect((state: State) => ({
  currentTheme: state.currentTheme,
}))(DynamicData) as any) as typeof DynamicData;
